"use client";

import posthog from "posthog-js";
import { useConsentStore } from "@/lib/analytics/consent";

// Export the official usePostHog hook for modern usage
export { usePostHog } from "posthog-js/react";

// User traits for identification
export interface UserTraits {
	// Basic user data
	email?: string;

	// Demographic data (privacy-safe)
	account_created: string;
	account_type: "free" | "premium";

	// Engagement metrics
	festivals_attended: number;
	reviews_written: number;
	friends_count: number;
	schedules_created: number;

	// Behavioral segments
	user_segment: "new" | "active" | "power" | "dormant";
	primary_genre?: string;
	home_region?: string;

	// Growth metrics
	referral_source?: string;
	activation_completed: boolean;
	has_invited_friends: boolean;
}

// Check if PostHog is initialized and consent is given
const canTrack = (): boolean => {
	if (typeof window === "undefined") return false;
	return useConsentStore.getState().canTrackPostHog() && posthog.__loaded;
};

// Check if we're in production
const isProduction = (): boolean => {
	return process.env.NODE_ENV === "production";
};

/**
 * Identify user for logged-in users
 * Call this when user logs in or when their profile data changes
 */
export const identifyUser = (
	userId: string,
	traits?: Partial<UserTraits>,
): void => {
	if (!canTrack()) return;

	try {
		posthog.identify(userId, {
			...traits,
			first_seen: new Date().toISOString(),
			platform: "web",
			// Add any additional common traits
		});
	} catch (error) {
		console.error("[PostHog] Error identifying user:", error);
	}
};

/**
 * Reset user identification (call on logout)
 */
export const resetUser = (): void => {
	if (!canTrack()) return;

	try {
		posthog.reset();
	} catch (error) {
		console.error("[PostHog] Error resetting user:", error);
	}
};

/**
 * Track user events with consent check
 * This is the main function for tracking PostHog events
 */
export const trackPostHog = (
	event: string,
	properties?: Record<string, unknown>,
): void => {
	// Only track in production (to keep data clean)
	if (!isProduction()) return;

	if (!canTrack()) return;

	try {
		// Add common properties to all events
		const commonProperties = {
			$time: new Date().toISOString(),
			platform: "web",
			user_agent: navigator.userAgent,
			...properties,
		};

		posthog.capture(event, commonProperties);
	} catch (error) {
		console.error("[PostHog] Error tracking event:", error);
	}
};

/**
 * Track page views manually (for privacy control)
 */
export const trackPageView = (
	path: string,
	properties?: Record<string, unknown>,
): void => {
	if (!canTrack()) return;

	try {
		posthog.capture("$pageview", {
			$current_url: window.location.href,
			$pathname: path,
			$title: document.title,
			...properties,
		});
	} catch (error) {
		console.error("[PostHog] Error tracking page view:", error);
	}
};

/**
 * Track user activation events
 * These are key events that indicate the user has realized product value
 */
export const trackUserActivation = (
	activationEvent:
		| "first_schedule_created"
		| "first_review_written"
		| "first_friend_added",
	properties?: Record<string, unknown>,
): void => {
	trackPostHog("user_activated", {
		activation_event: activationEvent,
		days_since_signup: properties?.days_since_signup || 0,
		...properties,
	});
};

/**
 * Track user retention events
 */
export const trackUserRetention = (
	daysSinceLastVisit: number,
	returnTrigger: "notification" | "email" | "organic" | "direct",
	properties?: Record<string, unknown>,
): void => {
	trackPostHog("user_returned", {
		days_since_last_visit: daysSinceLastVisit,
		return_trigger: returnTrigger,
		...properties,
	});
};

/**
 * Track user reactivation (coming back after being dormant)
 */
export const trackUserReactivation = (
	dormantDays: number,
	reactivationSource: string,
	properties?: Record<string, unknown>,
): void => {
	trackPostHog("user_reactivated", {
		dormant_days: dormantDays,
		reactivation_source: reactivationSource,
		...properties,
	});
};

/**
 * Track MAU/DAU core actions
 * These actions define what makes a user "active"
 */
export const trackCoreAction = (
	action:
		| "festival_viewed"
		| "schedule_modified"
		| "review_submitted"
		| "social_interaction"
		| "search_performed"
		| "festival_interaction"
		| "schedule_management"
		| "review_activity"
		| "social_engagement"
		| "content_discovery"
		| "daily_active_user"
		| "monthly_active_user"
		| "festival_lineup_explored"
		| "set_interaction"
		| "schedule_exported"
		| "artist_discovery"
		| "user_followed"
		| "global_search"
		| "search_result_clicked",
	properties?: Record<string, unknown>,
): void => {
	trackPostHog("user_active", {
		action,
		...properties,
	});
};

/**
 * Track viral actions that can lead to growth
 */
export const trackViralAction = (
	action:
		| "invitation_sent"
		| "invitation_accepted"
		| "social_share"
		| "referral_link_shared",
	properties?: Record<string, unknown>,
): void => {
	trackPostHog("viral_action", {
		action,
		...properties,
	});
};

/**
 * Track feature adoption
 */
export const trackFeatureAdoption = (
	feature: string,
	action: "first_use" | "regular_use" | "advanced_use",
	properties?: Record<string, unknown>,
): void => {
	trackPostHog("feature_adoption", {
		feature,
		action,
		...properties,
	});
};

/**
 * Track user lifecycle events
 */
export const trackUserLifecycle = (
	event:
		| "user_signed_up"
		| "user_onboarded"
		| "user_converted"
		| "user_churned",
	properties?: Record<string, unknown>,
): void => {
	trackPostHog(event, properties);
};

/**
 * Track growth experiments
 */
export const trackExperiment = (
	experimentName: string,
	variant: string,
	properties?: Record<string, unknown>,
): void => {
	trackPostHog("experiment_exposure", {
		experiment_name: experimentName,
		variant,
		...properties,
	});
};

/**
 * Track content creation (high-value actions)
 */
export const trackContentCreation = (
	contentType: "review" | "schedule" | "photo" | "playlist",
	properties?: Record<string, unknown>,
): void => {
	trackPostHog("content_created", {
		content_type: contentType,
		...properties,
	});
};

/**
 * Track social interactions
 */
export const trackSocialInteraction = (
	interaction: "follow" | "unfollow" | "like" | "share" | "comment" | "mention",
	properties?: Record<string, unknown>,
): void => {
	trackPostHog("social_interaction", {
		interaction_type: interaction,
		...properties,
	});
};

/**
 * Set user properties (for segmentation)
 */
export const setUserProperties = (
	properties: Record<string, unknown>,
): void => {
	if (!canTrack()) return;

	try {
		posthog.setPersonProperties(properties);
	} catch (error) {
		console.error("[PostHog] Error setting user properties:", error);
	}
};

/**
 * Track group properties (for B2B features like festival groups)
 */
export const trackGroup = (
	groupType: "festival" | "artist" | "venue",
	groupId: string,
	properties?: Record<string, unknown>,
): void => {
	if (!canTrack()) return;

	try {
		posthog.group(groupType, groupId, properties);
	} catch (error) {
		console.error("[PostHog] Error tracking group:", error);
	}
};


/**
 * Flush events (useful for critical events like purchases)
 */
export const flushEvents = (): void => {
	if (!canTrack()) return;

	try {
		// PostHog automatically flushes events, but we can force a flush if needed
		// This is a no-op for now since posthog.flush() is not available in the current version
	} catch (error) {
		console.error("[PostHog] Error flushing events:", error);
	}
};

// Export the posthog instance for advanced usage
export { posthog };
